import type { ISbStoryData } from '@storyblok/js';
import type { ProductDetailPageStoryblok } from '$types/bloks';
import type { DynamicPage } from '$pages/dynamic-page.svelte';
import type { Region } from '$lib/region';
import type { GetProductQuery } from '$gql/storefront/operations';
import { getFetchers } from '$lib/shopify';
import { transformProductData } from '$lib/utils/shopify/transformProductData';
import { LanguageCode } from '$gql/storefront/schemas';
import { getAdminProductByHandleClient } from './api/product-admin-api.client';

type ProductDetailPageStory = ISbStoryData<ProductDetailPageStoryblok>;

export type ProductDetailPage = {
  story: ProductDetailPageStory;
  product: NonNullable<GetProductQuery['product']>;
  handle: string;
  isAdminProduct: boolean;
};

export const isProductDetailPageStory = (
  story: DynamicPage['story']
): story is ProductDetailPageStory => {
  return story.content.component === 'product_detail_page';
};

export const isProductDetailPage = (data: DynamicPage): data is ProductDetailPage => {
  return data.story.content.component === 'product_detail_page';
};

export const PRODUCT_DETAIL_PAGE_RELATIONS = [
  'product_detail_page.recommendations',
  'product_detail_page.synced_size_table'
] as const;

export const getProductDetailPageData = async ({
  story,
  region,
  handle,
  lang = 'En',
  fetch,
  ab_test_version
}: {
  story: ProductDetailPageStory;
  region: Region;
  handle: string;
  lang: keyof typeof LanguageCode;
  fetch: typeof globalThis.fetch;
  ab_test_version: string;
}): Promise<ProductDetailPage> => {
  /**
   * PRODUCT FETCH
   */
  try {
    const shopifySlug = story.content.shopify_slug;
    const productQueryRes = await getFetchers(region, ab_test_version).getProduct({
      handle: shopifySlug || handle,
      language: LanguageCode[lang]
    });

    let product = productQueryRes.product;
    let adminProduct = undefined;
    if (!product) {
      adminProduct = await getAdminProductByHandleClient(shopifySlug || handle, region, fetch);
      if (adminProduct) {
        product = transformProductData(adminProduct);
      }
    }

    if (!product) {
      throw new Error("Couldn't fetch product with handle: " + handle);
    }

    return {
      story,
      product: product,
      handle,
      isAdminProduct: adminProduct != null
    };
  } catch {
    throw new Error("Couldn't fetch product with handle: " + handle);
  }
};
